'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import ForumHeader from '@/components/forum/ForumHeader';
import AttachmentInput from '@/components/forum/AttachmentInput';
import { useForumAuth } from '@/lib/useForumAuth';
import { enqueueWrite } from '@/lib/clientWrite';
import { CATEGORIES, FORUM_LIMITS, catColor } from '@/lib/forumConstants';
import type { UIThread, UIAttachment } from '@/components/forum/types';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const SORTS = [
  { key: 'active', label: 'Active' },
  { key: 'latest', label: 'Latest' },
];

export default function ForumPage() {
  const { currentUser, login } = useForumAuth();
  const [threads, setThreads] = useState<UIThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('');
  const [sort, setSort] = useState('active');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (sort) params.set('sort', sort);
      if (query.trim()) params.set('q', query.trim());
      const res = await fetch(`/api/threads?${params.toString()}`);
      const data = await res.json();
      if (data && Array.isArray(data.threads)) setThreads(data.threads);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category, sort, query]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  return (
    <>
      <ForumHeader />
      <main className="main-content forum-main">
        <div className="forum-hero">
          <div>
            <h1 className="forum-hero-title">Developer Forum</h1>
            <p className="forum-hero-sub">
              Share scripts, releases, and help across Roblox, Lua, JS, Python — any language. Post
              files, images, and videos.
            </p>
          </div>
          <button
            className="forum-new-btn"
            onClick={() => (currentUser ? setComposing((v) => !v) : login())}
          >
            {composing ? 'Close' : '+ New Thread'}
          </button>
        </div>

        {composing && currentUser && (
          <NewThreadForm
            onLoginNeeded={login}
            onCreated={(t) => {
              setThreads((prev) => [t, ...prev]);
              setComposing(false);
            }}
          />
        )}

        <div className="forum-toolbar">
          <div className="forum-cats">
            <button
              className={`forum-cat-pill ${category === '' ? 'active' : ''}`}
              onClick={() => setCategory('')}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                className={`forum-cat-pill ${category === c ? 'active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="forum-toolbar-right">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(search);
              }}
              className="forum-search"
            >
              <input
                placeholder="Search threads…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
            <div className="forum-sort">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  className={`forum-sort-btn ${sort === s.key ? 'active' : ''}`}
                  onClick={() => setSort(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="forum-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="forum-thread-row skeleton-card">
                <div className="skeleton-block" style={{ height: 16, width: '50%' }} />
                <div className="skeleton-block" style={{ height: 10, width: '30%', marginTop: 8 }} />
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="forum-empty">
            No threads yet{category ? ` in ${category}` : ''}. Be the first to post.
          </div>
        ) : (
          <div className="forum-thread-list">
            {threads.map((t) => {
              const score = t.upvotes.length - t.downvotes.length;
              return (
                <Link key={t._id} href={`/forum/${t._id}`} className="forum-thread-row">
                  <div className="forum-row-avatar">
                    {t.author?.avatar ? (
                      <img src={t.author.avatar} alt={t.author.username} />
                    ) : (
                      (t.author?.username || 'U')[0].toUpperCase()
                    )}
                  </div>
                  <div className="forum-thread-main">
                    <div className="forum-thread-titlerow">
                      {t.pinned && <span className="forum-badge pinned">Pinned</span>}
                      {t.locked && <span className="forum-badge locked">Locked</span>}
                      <span className="forum-thread-title">{t.title}</span>
                    </div>
                    <div className="forum-thread-meta">
                      <span className="forum-cat-chip">
                        <span className="forum-cat-dot" style={{ background: catColor(t.category) }} />
                        {t.category}
                      </span>
                      {t.tags?.slice(0, 4).map((tag) => (
                        <span key={tag} className="forum-headtag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="forum-row-stats">
                    <div className="forum-row-stat">
                      <span className="forum-row-statnum">{score}</span>
                      <span className="forum-row-statlbl">votes</span>
                    </div>
                    <div className="forum-row-stat">
                      <span className="forum-row-statnum">{t.replyCount}</span>
                      <span className="forum-row-statlbl">replies</span>
                    </div>
                    <span className="forum-row-time">{timeAgo(t.lastActivityAt || t.createdAt)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </>
  );
}

function NewThreadForm({
  onCreated,
  onLoginNeeded,
}: {
  onCreated: (t: UIThread) => void;
  onLoginNeeded: () => void;
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>(CATEGORIES[0]);
  const [body, setBody] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [attachments, setAttachments] = useState<UIAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!body.trim() && attachments.length === 0) {
      setError('Add a body or at least one attachment.');
      return;
    }
    setSubmitting(true);
    setError('');
    const tags = tagsText
      .split(/[,\s]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      const res = await enqueueWrite(() =>
        fetch('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title.trim(), category, body, tags, attachments }),
        })
      );
      const data = await res.json();
      if (res.ok && data && !data.error) {
        onCreated(data);
      } else if (res.status === 401) {
        onLoginNeeded();
      } else {
        setError(data.error || 'Failed to create thread.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="forum-compose">
      <div className="forum-compose-row">
        <input
          className="forum-input"
          placeholder="Thread title"
          value={title}
          maxLength={FORUM_LIMITS.titleMax}
          onChange={(e) => setTitle(e.target.value)}
        />
        <select className="forum-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="forum-textarea"
        placeholder="Write your post… Markdown supported (```lua code blocks```, **bold**, links)."
        value={body}
        maxLength={FORUM_LIMITS.bodyMax}
        onChange={(e) => setBody(e.target.value)}
      />
      <input
        className="forum-input"
        placeholder="Tags (comma or space separated, e.g. aimbot esp open-source)"
        value={tagsText}
        onChange={(e) => setTagsText(e.target.value)}
      />
      <AttachmentInput attachments={attachments} onChange={setAttachments} onLoginNeeded={onLoginNeeded} />
      {error && <div className="forum-error">{error}</div>}
      <div className="forum-compose-actions">
        <button type="submit" className="forum-new-btn" disabled={submitting}>
          {submitting ? 'Posting…' : 'Post Thread'}
        </button>
      </div>
    </form>
  );
}
